import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';

import Header from '@/common/header';
import { HomeIntlProvider, homeIntlTrialHref, useHomeIntl, useHomeVersao } from '@/components/home-nova/shared/home-intl-context';
import { lazyHydrated } from '@/components/home-nova/shared/lazy-hydrate';
import { HomeIntlI18nBoundary } from '@/components/home-nova/shared/pair-i18n';
import type { HomeVariantProps, HomeVisual } from '@/interface/home-pages';
import { AffiliateSdkScript } from '@/templates/main';
import type { HomeIntlPage } from '@/utils/home-intl/types';

// Os blocos da Home nova, como em src/pages/intl-home/[pais]/[idioma].tsx do staging. Shell, cabeçalho e hero hidratam com
// a página; da 1ª tela para baixo (#videos até o rodapé), hidratação tardia (src/components/home-nova/shared/lazy-hydrate.tsx):
// o servidor renderiza cada bloco por dynamic() (HTML inteiro e o CSS do bloco no <head>); no navegador só o import() do
// mesmo módulo, chamado quando o bloco chega perto da tela, recebe interação ou é alvo de âncora. O import() e o dynamic()
// de cada bloco têm de pedir o MESMO módulo (mesma entrada no react-loadable-manifest).
const HomeNovaShell = dynamic(() => import('@/components/home-nova/shell').then((mod) => mod.HomeNovaShell));
const HomeNovaHero = dynamic(() => import('@/components/home-nova/hero').then((mod) => mod.HomeNovaHero));
const isServer = typeof window === 'undefined';
const HomeNovaVideos = lazyHydrated('videos', () => import('@/components/home-nova/videos').then((mod) => mod.HomeNovaVideos), isServer ? dynamic(() => import('@/components/home-nova/videos').then((mod) => mod.HomeNovaVideos)) : null);
// Variantes com o visual v23 (home-v3/home-v4): a dobra 2 é o app da demo do hero, no lugar do bloco de vídeos.
const HomeNovaVersaoApp = lazyHydrated('versao-app', () => import('@/components/home-nova/versao-app').then((mod) => mod.HomeNovaVersaoApp), isServer ? dynamic(() => import('@/components/home-nova/versao-app').then((mod) => mod.HomeNovaVersaoApp)) : null);
const HomeNovaTecnologia = lazyHydrated('tecnologia', () => import('@/components/home-nova/tecnologia').then((mod) => mod.HomeNovaTecnologia), isServer ? dynamic(() => import('@/components/home-nova/tecnologia').then((mod) => mod.HomeNovaTecnologia)) : null);
const HomeNovaProPlayers = lazyHydrated('pro-players', () => import('@/components/home-nova/pro-players').then((mod) => mod.ProPlayersSection), isServer ? dynamic(() => import('@/components/home-nova/pro-players').then((mod) => mod.ProPlayersSection)) : null);
const HomeNovaJogos = lazyHydrated('jogos', () => import('@/components/home-nova/jogos').then((mod) => mod.HomeNovaJogos), isServer ? dynamic(() => import('@/components/home-nova/jogos').then((mod) => mod.HomeNovaJogos)) : null);
const HomeNovaCobertura = lazyHydrated('cobertura', () => import('@/components/home-nova/cobertura').then((mod) => mod.HomeNovaCobertura), isServer ? dynamic(() => import('@/components/home-nova/cobertura').then((mod) => mod.HomeNovaCobertura)) : null);
const HomeNovaFpsBooster = lazyHydrated('fps-booster', () => import('@/components/home-nova/fps-booster').then((mod) => mod.HomeNovaFpsBooster), isServer ? dynamic(() => import('@/components/home-nova/fps-booster').then((mod) => mod.HomeNovaFpsBooster)) : null);
const HomeNovaJogosPorGame = lazyHydrated('jogos-por-game', () => import('@/components/home-nova/jogos-por-game').then((mod) => mod.HomeNovaJogosPorGame), isServer ? dynamic(() => import('@/components/home-nova/jogos-por-game').then((mod) => mod.HomeNovaJogosPorGame)) : null);
const HomeNovaFaq = lazyHydrated('faq', () => import('@/components/home-nova/faq').then((mod) => mod.HomeNovaFaq), isServer ? dynamic(() => import('@/components/home-nova/faq').then((mod) => mod.HomeNovaFaq)) : null);
// Header e footer são os do site (decisão do dono, docs/home-variantes.md): o header com a página, o footer por dynamic(),
// como na home de antes.
const Footer = dynamic(() => import('@/common/footer'));
// Fonte Noto só da escrita da página (árabe, hebraica, CJK...; src/components/home-nova/intl/script-font.tsx).
const HomeIntlScriptFont = dynamic(() => import('@/components/home-nova/intl/script-font').then((mod) => mod.HomeIntlScriptFont));
const HomeNovaSeo = dynamic(() => import('@/components/home-nova/seo').then((mod) => mod.HomeNovaSeo));

// O rótulo do link "Pular para o conteúdo" do shell continua no namespace header-footer da Home nova (só ele é lido daí).
export const HEADER_FOOTER_NAMESPACE = 'components/home-nova/header-footer';
const VIDEOS_ANCHOR = '#videos';

interface HomeNovaBodyProps {
  /** Visual da variante: `app` = app da demo no hero e bloco de vídeos na dobra 2; `v23` = trailer no hero e app na dobra 2. */
  visual: HomeVisual;
  preview: boolean;
  escrita?: HomeIntlPage['escrita'];
  campaignHero?: ReactNode;
}

function HomeNovaBody({ visual, preview, escrita, campaignHero }: HomeNovaBodyProps) {
  const { t } = useTranslation(HEADER_FOOTER_NAMESPACE);
  // Nas versões "Assine" (do par ou da Home do site), o CTA do header e o do footer acompanham o do hero (rótulo da versão, link para Planos e preços), como
  // no header da Home nova do Marcos.
  const intlPage = useHomeIntl();
  const versao = useHomeVersao();
  // `null` nas outras: a Home decide pela versão que mostra, e não pelo cookie que as demais páginas seguem (SCRUM-2631).
  const headerCta = versao?.cta === 'assine' && versao.textos.assine ? { label: versao.textos.assine, href: intlPage ? homeIntlTrialHref(intlPage) : '/pricing' } : null;

  return (
    <>
      <AffiliateSdkScript />
      <HomeNovaSeo preview={preview} />
      <HomeNovaShell header={<Header variant='primary' primaryCta={headerCta} />} footer={<Footer primaryCta={headerCta} />} skipLinkLabel={t('skipLink')}>
        {escrita ? <HomeIntlScriptFont escrita={escrita} /> : null}
        {campaignHero ?? <HomeNovaHero moreHref={VIDEOS_ANCHOR} />}
        {visual === 'v23' ? <HomeNovaVersaoApp /> : <HomeNovaVideos />}
        <HomeNovaTecnologia />
        <HomeNovaProPlayers />
        <HomeNovaJogos />
        <HomeNovaCobertura />
        <HomeNovaFpsBooster />
        <HomeNovaJogosPorGame />
        <HomeNovaFaq />
      </HomeNovaShell>
    </>
  );
}

export interface HomeNovaPageProps extends HomeVariantProps {
  /**
   * Página do par `/{pais}/{idioma}`: textos do bundle aprovado e, nas variantes 2–4, `versao` com os textos que a variante
   * troca (rótulo "Assine agora", trailer v23, dobra 2 do app), carregados no servidor para a mesma variante. Ausente na `/`
   * dos locales do site, que só serve a home-v1.
   */
  homeIntlPage?: HomeIntlPage;
  /** Rota de QA (/preview/home/[key]): a página sai noindex, com o canonical na Home do par. */
  preview?: boolean;
  /** Hero de uma campanha ativa (gamers day, black friday, natal) no lugar do hero da Home nova; só na `/` dos locales. */
  campaignHero?: ReactNode;
}

/**
 * A Home nova inteira. Num par, os textos vêm do bundle aprovado num idioma i18next só dele (pair-i18n.tsx: sem idioma
 * de reserva, iguais no servidor e no navegador); na `/` e nos locales do site, dos namespaces
 * `public/locales/<locale>/components/home-nova/*`. Quem escolhe a variante é o servidor (na `/`, o rodízio da api em src/pages/index.tsx);
 * esta página só a desenha pelas props do HomeVariantSwitcher: a dobra 2 pelo `visual`, o hero e o CTA pelo `versao` do par.
 */
export function HomeNovaPage({ visual, homeIntlPage, preview = false, campaignHero }: HomeNovaPageProps) {
  if (!homeIntlPage) return <HomeNovaBody visual={visual} preview={preview} campaignHero={campaignHero} />;

  return (
    <HomeIntlI18nBoundary lng={homeIntlPage.i18nLng}>
      <HomeIntlProvider page={homeIntlPage}>
        <HomeNovaBody visual={visual} preview={preview} escrita={homeIntlPage.escrita} />
      </HomeIntlProvider>
    </HomeIntlI18nBoundary>
  );
}
